import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Phone,
  Search,
  PlusCircle,
  Trash2,
  RefreshCcw,
  ShoppingCart,
  Building2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PhoneNumbersTab() {
  const [numbers, setNumbers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search dialog
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchAreaCode, setSearchAreaCode] = useState("");
  const [searchContains, setSearchContains] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [buying, setBuying] = useState(null);

  // Manual add dialog
  const [manualOpen, setManualOpen] = useState(false);
  const [manualNumber, setManualNumber] = useState("");
  const [manualOrg, setManualOrg] = useState("");
  const [manualSaving, setManualSaving] = useState(false);

  // Purchase assign
  const [pendingPurchase, setPendingPurchase] = useState(null);
  const [purchaseOrg, setPurchaseOrg] = useState("");

  // Reassign
  const [reassignRow, setReassignRow] = useState(null);
  const [reassignOrg, setReassignOrg] = useState("");

  const fetchNumbers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/platform/phone-numbers`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setNumbers(data.numbers || []);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgs = async () => {
    try {
      const res = await fetch(`${API}/platform/organizations?limit=100`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setOrgs(data.organizations || []);
      }
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    fetchNumbers();
    fetchOrgs();
  }, []);

  const handleSearch = async () => {
    setSearching(true);
    setSearchResults([]);
    try {
      const qs = new URLSearchParams();
      if (searchAreaCode) qs.set("area_code", searchAreaCode);
      if (searchContains) qs.set("contains", searchContains);
      qs.set("limit", "15");
      const res = await fetch(`${API}/platform/phone-numbers/available?${qs}`, {
        credentials: "include",
      });
      const data = await res.json();
      if (res.ok) {
        setSearchResults(data.numbers || []);
        if (!data.numbers?.length) toast.message("No numbers found for that filter");
      } else {
        toast.error(data.detail || "Search failed");
      }
    } catch {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const handlePurchase = async (num, orgId) => {
    setBuying(num.phone_number);
    try {
      const res = await fetch(`${API}/platform/phone-numbers/purchase`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone_number: num.phone_number,
          friendly_name: num.friendly_name,
          org_id: orgId || null,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Purchased ${num.phone_number}`);
        setPendingPurchase(null);
        setSearchResults((prev) => prev.filter((n) => n.phone_number !== num.phone_number));
        fetchNumbers();
      } else {
        toast.error(data.detail || "Purchase failed");
      }
    } catch {
      toast.error("Purchase failed");
    } finally {
      setBuying(null);
    }
  };

  const handleManualAdd = async () => {
    if (!manualNumber.trim()) {
      toast.error("Phone number is required");
      return;
    }
    setManualSaving(true);
    try {
      const res = await fetch(`${API}/platform/phone-numbers`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone_number: manualNumber.trim(),
          org_id: manualOrg || null,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Number added");
        setManualOpen(false);
        setManualNumber("");
        setManualOrg("");
        fetchNumbers();
      } else {
        toast.error(data.detail || "Failed to add");
      }
    } finally {
      setManualSaving(false);
    }
  };

  const handleReassign = async () => {
    if (!reassignRow) return;
    try {
      const res = await fetch(
        `${API}/platform/phone-numbers/${reassignRow.phone_sid}/assign`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ org_id: reassignOrg || null }),
        }
      );
      const data = await res.json();
      if (res.ok) {
        toast.success("Number reassigned");
        setReassignRow(null);
        fetchNumbers();
      } else {
        toast.error(data.detail || "Failed");
      }
    } catch {
      toast.error("Failed");
    }
  };

  const handleRelease = async (row) => {
    if (!window.confirm(`Release ${row.phone_number}? This returns it to Twilio.`)) return;
    try {
      const res = await fetch(`${API}/platform/phone-numbers/${row.phone_sid}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        toast.success("Number released");
        fetchNumbers();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed");
      }
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Phone className="w-5 h-5" />
              Phone Numbers
            </CardTitle>
            <CardDescription>
              Provision Twilio numbers and assign them to organizations.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setSearchOpen(true)}
              data-testid="search-numbers-btn"
            >
              <Search className="w-4 h-4 mr-2" />
              Search &amp; Buy
            </Button>
            <Button onClick={() => setManualOpen(true)} data-testid="add-manual-number-btn">
              <PlusCircle className="w-4 h-4 mr-2" />
              Add Manually
            </Button>
            <Button variant="ghost" size="icon" onClick={fetchNumbers}>
              <RefreshCcw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : numbers.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Phone className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>No phone numbers provisioned yet.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Number</TableHead>
                <TableHead>Friendly Name</TableHead>
                <TableHead>Assigned Org</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Added</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {numbers.map((n) => (
                <TableRow key={n.phone_sid} data-testid={`number-row-${n.phone_sid}`}>
                  <TableCell className="font-mono font-medium">{n.phone_number}</TableCell>
                  <TableCell className="text-sm text-slate-600">{n.friendly_name}</TableCell>
                  <TableCell>
                    {n.org_name ? (
                      <Badge variant="secondary" className="text-xs">
                        <Building2 className="w-3 h-3 mr-1" />
                        {n.org_name}
                      </Badge>
                    ) : (
                      <span className="text-xs text-slate-400 italic">Unassigned</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {n.source}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {n.created_at ? new Date(n.created_at).toLocaleDateString() : "—"}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setReassignRow(n);
                        setReassignOrg(n.org_id || "");
                      }}
                      data-testid={`reassign-${n.phone_sid}`}
                    >
                      Reassign
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="text-red-600"
                      onClick={() => handleRelease(n)}
                      data-testid={`release-${n.phone_sid}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Search & Buy Dialog */}
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Search Available Numbers</DialogTitle>
            <DialogDescription>
              Filter Twilio's inventory by area code or pattern, then purchase.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Area Code</Label>
              <Input
                placeholder="e.g. 415"
                value={searchAreaCode}
                onChange={(e) => setSearchAreaCode(e.target.value)}
                data-testid="search-area-code"
              />
            </div>
            <div>
              <Label>Contains</Label>
              <Input
                placeholder="e.g. 1234 or LOVE"
                value={searchContains}
                onChange={(e) => setSearchContains(e.target.value)}
              />
            </div>
          </div>
          <Button onClick={handleSearch} disabled={searching} data-testid="run-search-btn">
            <Search className="w-4 h-4 mr-2" />
            {searching ? "Searching..." : "Search"}
          </Button>

          {searchResults.length > 0 && (
            <div className="max-h-80 overflow-y-auto border rounded">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Number</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {searchResults.map((n) => (
                    <TableRow key={n.phone_number}>
                      <TableCell className="font-mono">{n.phone_number}</TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {n.locality || n.region || "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {pendingPurchase?.phone_number === n.phone_number ? (
                          <div className="flex gap-2 items-center">
                            <Select value={purchaseOrg} onValueChange={setPurchaseOrg}>
                              <SelectTrigger className="w-40">
                                <SelectValue placeholder="Assign to org" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">Unassigned</SelectItem>
                                {orgs.map((o) => (
                                  <SelectItem key={o.org_id} value={o.org_id}>
                                    {o.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              size="sm"
                              onClick={() =>
                                handlePurchase(n, purchaseOrg === "__none__" ? null : purchaseOrg)
                              }
                              disabled={buying === n.phone_number}
                            >
                              {buying === n.phone_number ? "Buying..." : "Confirm"}
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setPendingPurchase(n);
                              setPurchaseOrg("");
                            }}
                            data-testid={`buy-${n.phone_number}`}
                          >
                            <ShoppingCart className="w-3 h-3 mr-1" />
                            Buy
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Manual Add Dialog */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Existing Number</DialogTitle>
            <DialogDescription>
              For numbers already in your Twilio account or BYO.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Phone Number (E.164)</Label>
              <Input
                placeholder="+15551234567"
                value={manualNumber}
                onChange={(e) => setManualNumber(e.target.value)}
                data-testid="manual-number-input"
              />
            </div>
            <div>
              <Label>Assign to Organization</Label>
              <Select value={manualOrg} onValueChange={setManualOrg}>
                <SelectTrigger>
                  <SelectValue placeholder="Select org (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Unassigned</SelectItem>
                  {orgs.map((o) => (
                    <SelectItem key={o.org_id} value={o.org_id}>
                      {o.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (manualOrg === "__none__") setManualOrg("");
                handleManualAdd();
              }}
              disabled={manualSaving}
              data-testid="save-manual-number"
            >
              {manualSaving ? "Saving..." : "Add Number"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Dialog */}
      <Dialog open={!!reassignRow} onOpenChange={(o) => !o && setReassignRow(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reassign {reassignRow?.phone_number}</DialogTitle>
          </DialogHeader>
          <div>
            <Label>Assign to Organization</Label>
            <Select value={reassignOrg} onValueChange={setReassignOrg}>
              <SelectTrigger data-testid="reassign-select">
                <SelectValue placeholder="Select org" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {orgs.map((o) => (
                  <SelectItem key={o.org_id} value={o.org_id}>
                    {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignRow(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (reassignOrg === "__none__") setReassignOrg("");
                handleReassign();
              }}
              data-testid="confirm-reassign"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
